import React, { useEffect, useState } from "react";
import { Table, Collapse, Row, Popover, Col, Button, Spin } from "antd";
import { cloneDeep, get, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, formatNumber, showError, showSuccess } from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import Filter from "../../../components/functional_components/Filter/Filter";
import { useFormik } from "formik";
import { langMonth, langDate } from "../../../utils/properties";
import { DownloadOutlined, FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";
import "./report.css";
import WorkProgressSupplier from "../../../services/api/WorkProgressSupplier";
import dayjs from "dayjs";

const ReportSuppliers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const currentDate = dayjs().format("YYYYMM");
  const prevMonth = dayjs(currentDate, "YYYYMM").subtract(1, "month").endOf("month").format("YYYYMM");
  const [state, setState] = useState({
    panelContent: [],
    costCenterFilter: [],
    suppliersOptionsList: [],
    loading: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString({}, { initialMonth: prevMonth, endMonth: currentDate });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const dataList = async (queryString) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const report = await WorkProgressSupplier.getSupplierReport(queryString);
    if (!report.error && report.data && report.data.months) {
      report.data.months.forEach((cc, i) => {
        cc.orders.forEach((ccc, j) => {
          ccc.key = `${i}${j}`; // This is necessary to remove warning in console for unique key row and I use it for open row table
        });
      });
      setState((prevState) => ({
        ...prevState,
        panelContent: get(report, "data.months", []),
      }));
    } else {
      showError(get(report, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const resetFilter = () => {
    const queryString = buildQueryString({}, { initialMonth: prevMonth, endMonth: currentDate });
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const columns = [
    {
      title: t("report.table.oda"),
      dataIndex: "id",
      key: "id",
    },
    {
      align: "right",
      title: t("report.table.monthValue"),
      dataIndex: "monthValue",
      key: "monthValue",
      className: "table-value-right",
      render: (value) =>
        formatNumber({
          value,
          lang: currentLanguage,
          suffix: "EUR",
        }),
    },
  ];

  const expandedRowRender = (dataRow) => {
    const columns = [
      {
        title: t("report.table.project"),
        dataIndex: "projectId",
        key: "projectId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectName}
            </div>
          );
        },
        responsive: ["sm"],
      },
      {
        title: t("report.table.projectTask"),
        dataIndex: "projectTaskId",
        key: "projectTaskId",
        render: (value, row) => {
          return (
            <div>
              {value} | {row.projectTaskName}
            </div>
          );
        },
      },
      {
        align: "right",
        title: t("report.table.amount"),
        dataIndex: "amount",
        key: "amount",
        className: "table-value-right",
        render: (value) =>
          formatNumber({
            value,
            lang: currentLanguage,
            suffix: "EUR",
          }),
      },
      {
        align: "right",
        title: t("report.table.hours"),
        dataIndex: "hours",
        key: "hours",
        className: "table-value-right",
        render: (value) =>
          formatNumber({
            value,
            lang: currentLanguage,
            suffix: "HOUR",
          }),
      },
    ];
    return (
      <>
        <Table columns={columns} dataSource={dataRow.projectTasks} pagination={false} rowKey="id" />
      </>
    );
  };

  const formikFilter = useFormik({
    initialValues: {
      rangeMonth: [dayjs(prevMonth, "YYYYMM"), dayjs(currentDate, "YYYYMM")],
    },
    enableReinitialize: true,
    validate: (values) => {
      let errors = {};
      if (!values.rangeMonth) {
        errors.rangeMonth = "Required!";
      }
      return errors;
    },
    onSubmit: (values) => {
      const filterSubmit = cloneDeep(values);
      delete filterSubmit.rangeMonth;
      filterSubmit.initialMonth = dayjs(values.rangeMonth[0], "YYYYMM").format("YYYYMM");
      filterSubmit.endMonth = dayjs(values.rangeMonth[1], "YYYYMM").format("YYYYMM");
      const queryString = buildQueryString(filters.obj, filterSubmit);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "rangeMonth",
      isRequired: true,
      id: "rangeMonth",
      name: "rangeMonth",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("report.filter.rangeMonth"),
      value: formikFilter.values.rangeMonth,
      onChange: (date) => {
        formikFilter.setFieldValue("rangeMonth", date);
      },
    },
  ];

  const exportReportSupplierCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await WorkProgressSupplier.downloadSupplierReportCsv(filters.string);
    if (!exportResponse.error) {
      showSuccess();
      const blob = new Blob([exportResponse]);
      exportFile(blob, "report.csv");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const exportReportSupplierPdf = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await WorkProgressSupplier.downloadSupplierReportPdf(filters.string);
    if (!exportResponse.error) {
      showSuccess();
      const blob = new Blob([exportResponse], { type: "application/pdf" });
      exportFile(blob, "report.pdf");
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const getExtra = (value) => {
    return formatNumber({
      value: value,
      lang: currentLanguage,
      suffix: "EUR",
    });
  };

  return (
    <>
      {state.loading ? (
        <div className="container-spinner-positioning">
          <Spin spinning={state.loading} className="spinner-positioning" />
        </div>
      ) : (
        ""
      )}

      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow={true} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Popover
          placement="bottomRight"
          title={t("report.popover.title")}
          content={
            <Row>
              <Col span={24}>
                <a className="download-link" onClick={exportReportSupplierPdf}>
                  <FilePdfOutlined className="pdf-icon" /> {t("report.popover.pdf")}
                </a>
              </Col>
              <Col span={24}>
                <a className="download-link" onClick={exportReportSupplierCsv}>
                  <FileExcelOutlined className="excel-icon" /> {t("report.popover.excel")}
                </a>
              </Col>
            </Row>
          }
          trigger="click"
        >
          <Button
            loading={state.downloadLoading}
            type="primary"
            className="pf-btn-primary"
            icon={<DownloadOutlined className="icon-medium" />}
          />{" "}
          {/* I must use antd button to use popover */}
        </Popover>
      </Row>
      <Collapse
        className="collapse-header"
        expandIconPosition="end"
        items={state.panelContent.map((p, i) => ({
          key: `${i}`, // Chiave unica
          label: dayjs(p.month.toString(), "YYYYMM").format(dateFormat), // Header del pannello
          extra: getExtra(p.total), // Contenuto extra
          children: (
            <Table
              pagination={false}
              columns={columns}
              expandable={{ expandedRowRender }}
              dataSource={p.orders}
              size="small"
            />
          ),
        }))}
      />
    </>
  );
};

export default ReportSuppliers;
